// Automatic pauses for single-player (pacing spec §4 A1/A2).
//
// Two triggers pause the sim on the player's behalf:
//   - away:   the tab is hidden, or the window stays unfocused for AWAY_GRACE_MS
//   - danger: detectDangers() reports an alert the pauseOnDanger setting cares about
// Each automatic pause opens a card; only the card's Resume (or the player
// resuming by hand) restarts the sim. The controller remembers *who* paused,
// so an automatic pause never resumes a game the player paused themselves.
// The milestone cinematic director borrows the same claim ('cinematic'):
// cinematicPause() only pauses a running game, cinematicResume() only
// resumes one the cinematic paused.

import type { GameSnapshot } from '@aod/protocol';
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import {
  type DangerAlert,
  type DangerState,
  EMPTY_DANGER_STATE,
  type PauseOnDanger,
  detectDangers,
  shouldPauseFor,
} from './detectDangers.js';

export type AutoPauseReason = 'away' | 'danger' | 'cinematic';
type RunState = 'stopped' | 'running' | 'paused';

/** A blur shorter than this (alt-tab peek, devtools click) doesn't pause. */
export const AWAY_GRACE_MS = 2000;

export type PauseCard =
  | { readonly kind: 'away'; readonly awayMs: number }
  | { readonly kind: 'danger'; readonly alerts: ReadonlyArray<DangerAlert> };

export interface PauseControllerOptions {
  readonly enabled: boolean;
  readonly runState: RunState;
  readonly pause: () => void;
  readonly resume: () => void;
  readonly snapshot: GameSnapshot | null;
  readonly tick: number;
  readonly localPlayer: number;
  readonly autoPauseWhenAway: boolean;
  readonly pauseOnDanger: PauseOnDanger;
  /** Injected for tests. */
  readonly now?: () => number;
}

export interface PauseController {
  readonly card: PauseCard | null;
  /** Resume from the open card (only resumes if we paused). */
  readonly resumeFromCard: () => void;
  /** Close the card without resuming (e.g. "Go to" keeps the game paused). */
  readonly dismissCard: () => void;
  /**
   * Pause for a milestone cinematic. Returns true when the game is paused
   * FOR the cinematic — this call paused it, or the pause was already ours
   * (a cinematic cutting in over another one). A game that is already paused
   * (by the player, a card or the end of the match) is left alone and the
   * cinematic must not resume it.
   */
  readonly cinematicPause: () => boolean;
  /** Resume after a cinematic, but only if the cinematic's pause still holds. */
  readonly cinematicResume: () => void;
}

export function usePauseController(opts: PauseControllerOptions): PauseController {
  const { enabled, runState, pause, resume, snapshot, tick, localPlayer } = opts;
  const now = opts.now ?? Date.now;
  const [card, setCard] = useState<PauseCard | null>(null);
  /** Non-null while the sim is paused because *we* paused it. */
  const autoReasonRef = useRef<AutoPauseReason | null>(null);
  const runStateRef = useRef(runState);
  runStateRef.current = runState;
  const awaySinceRef = useRef<number | null>(null);
  const dangerRef = useRef<DangerState>(EMPTY_DANGER_STATE);

  const autoPause = useCallback(
    (reason: AutoPauseReason): boolean => {
      if (runStateRef.current !== 'running') return false;
      autoReasonRef.current = reason;
      pause();
      return true;
    },
    [pause],
  );

  const resumeFromCard = useCallback(() => {
    setCard(null);
    if (autoReasonRef.current !== null && runStateRef.current === 'paused') resume();
    autoReasonRef.current = null;
  }, [resume]);

  const dismissCard = useCallback(() => setCard(null), []);

  const cinematicPause = useCallback((): boolean => {
    if (!enabled) return false;
    // The pause is already ours (a cinematic cutting in over another one):
    // the game is paused for the cinematic, so say so — but don't pause it
    // a second time.
    if (autoReasonRef.current === 'cinematic') return true;
    // Another auto-pause (away, danger) holds the game: not ours to take,
    // and the cinematic must not resume it.
    if (autoReasonRef.current !== null) return false;
    return autoPause('cinematic');
  }, [enabled, autoPause]);

  const cinematicResume = useCallback(() => {
    // The claim is dropped when the player resumes by hand (effect below) or
    // another auto-pause took over, so only a cinematic-held pause resumes.
    if (autoReasonRef.current !== 'cinematic') return;
    autoReasonRef.current = null;
    // runStateRef may still read 'running' if the pause hasn't re-rendered
    // yet (a skip on the first frame); resume() is a no-op when running.
    if (runStateRef.current !== 'stopped') resume();
  }, [resume]);

  // The player resumed by hand (TimeControls, Space): drop our claim and card.
  //
  // A LAYOUT effect, not a passive one. The claim must be gone in the SAME
  // commit that reports the game running again: a passive effect runs a task
  // later, and anything that claims the pause in that window (a milestone
  // cinematic offering itself) finds the claim still held and refuses — the
  // game then keeps simulating under the cinematic. A layout effect runs
  // synchronously after the commit, before the browser runs the next task.
  useLayoutEffect(() => {
    if (runState === 'running' && autoReasonRef.current !== null) {
      autoReasonRef.current = null;
      setCard(null);
    }
  }, [runState]);

  // ── away ──
  useEffect(() => {
    if (!enabled || !opts.autoPauseWhenAway) return;
    let graceTimer: ReturnType<typeof setTimeout> | null = null;

    const goAway = () => {
      if (awaySinceRef.current !== null) return;
      if (autoPause('away')) awaySinceRef.current = now();
    };
    const comeBack = () => {
      if (graceTimer) {
        clearTimeout(graceTimer);
        graceTimer = null;
      }
      const since = awaySinceRef.current;
      awaySinceRef.current = null;
      if (since !== null && autoReasonRef.current === 'away') {
        setCard({ kind: 'away', awayMs: now() - since });
      }
    };
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') goAway();
      else if (document.hasFocus()) comeBack();
    };
    const onBlur = () => {
      if (graceTimer) clearTimeout(graceTimer);
      graceTimer = setTimeout(goAway, AWAY_GRACE_MS);
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('blur', onBlur);
    window.addEventListener('focus', comeBack);
    return () => {
      if (graceTimer) clearTimeout(graceTimer);
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('blur', onBlur);
      window.removeEventListener('focus', comeBack);
    };
  }, [enabled, opts.autoPauseWhenAway, autoPause, now]);

  // ── danger ──
  // Diff every snapshot, even with the setting off, so turning it on
  // mid-match doesn't treat the whole match so far as new damage.
  const pauseOnDanger = opts.pauseOnDanger;
  const cardRef = useRef(card);
  cardRef.current = card;
  const lastTickRef = useRef(tick);
  useEffect(() => {
    // Tick went backwards: new match or loaded save. Re-seed, don't alert.
    if (tick < lastTickRef.current) dangerRef.current = EMPTY_DANGER_STATE;
    lastTickRef.current = tick;

    const { alerts, next } = detectDangers(dangerRef.current, snapshot, localPlayer, tick);
    dangerRef.current = next;
    if (!enabled) return;
    const relevant = alerts.filter((a) => shouldPauseFor(pauseOnDanger, a.severity));
    if (relevant.length === 0) return;

    const open = cardRef.current;
    if (open?.kind === 'danger') {
      // Merge into the open card instead of stacking cards.
      setCard({ kind: 'danger', alerts: mergeAlerts(open.alerts, relevant) });
    } else if (autoPause('danger')) {
      setCard({ kind: 'danger', alerts: relevant });
    }
  }, [snapshot, tick, localPlayer, enabled, pauseOnDanger, autoPause]);

  return { card, resumeFromCard, dismissCard, cinematicPause, cinematicResume };
}

/** Latest alert per (kind, city) wins; order of first appearance is kept. */
export function mergeAlerts(
  prev: ReadonlyArray<DangerAlert>,
  incoming: ReadonlyArray<DangerAlert>,
): DangerAlert[] {
  const byKey = new Map<string, DangerAlert>();
  for (const a of [...prev, ...incoming]) byKey.set(`${a.kind}:${a.cityId}`, a);
  return [...byKey.values()];
}
